import React, { useState, useEffect } from 'react';
import { sdrService } from '../../lib/services/sdr';
import SdrCalendarView from './SdrCalendarView';
import SdrRecordForm from './SdrRecordForm';
import SdrRecordDetails from './SdrRecordDetails';
import StatisticsPanel from './StatisticsPanel';
import './SdrModule.css';

const SdrModule = ({ currentUser, onBack, onTabChange }) => {
  const [currentView, setCurrentView] = useState('calendar'); // 'calendar', 'form', 'details'
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [sdrRecords, setSdrRecords] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({});

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [recordsData, teachersData, studentsData, statistics] = await Promise.all([
        sdrService.getSdrRecords(),
        sdrService.getTeachers(),
        sdrService.getStudents(),
        sdrService.getSdrStats()
      ]);

      setSdrRecords(recordsData);
      setTeachers(teachersData);
      setStudents(studentsData);
      setStats(statistics);
    } catch (err) {
      console.error('Error loading SDR data:', err);
      setError('載入SDR記錄資料時發生錯誤');
    } finally {
      setLoading(false);
    }
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setCurrentView('form');
  };

  const handleRecordSelect = (record) => {
    setSelectedRecord(record);
    setCurrentView('details');
  };

  const handleRecordSave = async (recordData) => {
    try {
      if (selectedRecord) {
        // Update existing record
        const updatedRecord = await sdrService.updateSdrRecord(selectedRecord.id, recordData);
        setSdrRecords(prev => prev.map(record => 
          record.id === selectedRecord.id ? updatedRecord : record
        ));
      } else {
        // Add new record
        const newRecord = await sdrService.addSdrRecord(recordData);
        setSdrRecords(prev => [...prev, newRecord]);
      }
      
      setSelectedRecord(null);
      setCurrentView('calendar');
      
      // Reload statistics
      const updatedStats = await sdrService.getSdrStats();
      setStats(updatedStats);
    } catch (err) {
      console.error('Error saving SDR record:', err);
      setError('儲存SDR記錄時發生錯誤');
    }
  };

  const handleRecordDelete = async (recordId) => {
    try {
      await sdrService.deleteSdrRecord(recordId);
      setSdrRecords(prev => prev.filter(record => record.id !== recordId));
      
      // Reload statistics
      const updatedStats = await sdrService.getSdrStats();
      setStats(updatedStats);
      
      setCurrentView('calendar');
      setSelectedRecord(null);
    } catch (err) {
      console.error('Error deleting SDR record:', err);
      setError('刪除SDR記錄時發生錯誤');
    }
  };

  const handleEditRecord = (record) => {
    setSelectedRecord(record);
    setCurrentView('form');
  };

  const handleBackToCalendar = () => {
    setCurrentView('calendar');
    setSelectedRecord(null);
  };

  if (loading) {
    return (
      <div className="sdr-module">
        <div className="sdr-header">
          <button className="back-btn" onClick={onBack}>
            ← 返回主頁
          </button>
          <h1>SDR 教師請假/進修/公幹記錄系統</h1>
        </div>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>載入SDR記錄中...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="sdr-module">
        <div className="sdr-header">
          <button className="back-btn" onClick={onBack}>
            ← 返回主頁
          </button>
          <h1>SDR 教師請假/進修/公幹記錄系統</h1>
        </div>
        <div className="error-container">
          <p>❌ {error}</p>
          <button onClick={loadInitialData} className="retry-btn">
            重新載入
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="sdr-module">
      <div className="sdr-header">
        <button className="back-btn" onClick={onBack}>
          ← 返回主頁
        </button>
        <h1>SDR 教師請假/進修/公幹記錄系統</h1>
        <div className="sdr-nav">
          <button 
            className={`nav-btn ${currentView === 'calendar' ? 'active' : ''}`}
            onClick={() => setCurrentView('calendar')}
          >
            📅 查看日曆
          </button>
          <button 
            className={`nav-btn ${currentView === 'form' ? 'active' : ''}`}
            onClick={() => {
              setSelectedRecord(null);
              setCurrentView('form');
            }}
          >
            📝 新增記錄
          </button>
        </div>
      </div>

      <div className="sdr-content">
        <StatisticsPanel stats={stats} />

        {currentView === 'calendar' && (
          <SdrCalendarView
            records={sdrRecords}
            selectedDate={selectedDate}
            onDateSelect={handleDateSelect}
            onRecordSelect={handleRecordSelect}
            onEditRecord={handleEditRecord}
            onDeleteRecord={handleRecordDelete}
          />
        )}

        {currentView === 'form' && (
          <SdrRecordForm
            record={selectedRecord}
            teachers={teachers}
            students={students}
            selectedDate={selectedDate}
            onSave={handleRecordSave}
            onCancel={handleBackToCalendar}
          />
        )}

        {currentView === 'details' && selectedRecord && (
          <SdrRecordDetails
            record={selectedRecord}
            onEdit={handleEditRecord}
            onDelete={handleRecordDelete}
            onBack={handleBackToCalendar}
          />
        )}
      </div>

      {/* Floating Action Button */}
      <div className="floating-controls">
        <div className="step-navigation">
          <div className="step-item">
            <span className="step-number">1</span>
            <span className="step-label">選擇日期</span>
          </div>
          <div className="step-item">
            <span className="step-number">2</span>
            <span className="step-label">填寫記錄</span>
          </div>
          <div className="step-item">
            <span className="step-number">3</span>
            <span className="step-label">確認提交</span>
          </div>
          <div className="step-item">
            <span className="step-number">4</span>
            <span className="step-label">完成</span>
          </div>
        </div>
        
        <button 
          className="floating-btn calendar-btn"
          onClick={() => setCurrentView('calendar')}
        >
          📅 SDR 日曆
        </button>
        
        <div className="stats-display">
          <div className="stat-item">
            <span className="stat-number">{stats.totalRecords}</span>
            <span className="stat-label">記錄總數</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">{stats.totalAffectedStudents}</span>
            <span className="stat-label">影響學生數</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SdrModule;
